/**
 * Note type normalization utilities
 * 
 * Maps raw document_type values to canonical category keys based on
 * the valid template categories from the database constraint (note_template.py):
 * clinical, assessment, summary, intake, progress, discharge, other
 * 
 * SOAP, DAP, and similar template formats are all "clinical" category notes.
 */

// Valid template categories from database constraint
export const CATEGORY_LABELS = {
  clinical: 'Clinical Note',
  assessment: 'Assessment',
  summary: 'Client Summary',
  intake: 'Intake Note',
  progress: 'Progress Note',
  discharge: 'Discharge Summary',
  reflection: 'Therapist Reflection',
  other: 'Other',
};

/** Badge colors per category — [bg, text, darkBg, darkText] Tailwind classes */
export const CATEGORY_BADGE_CLASSES: Record<string, string> = {
  clinical: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  assessment: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  summary: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  intake: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  progress: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  discharge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  reflection: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  other: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

/** Left-border accent color per category */
export const CATEGORY_BORDER_CLASSES: Record<string, string> = {
  clinical: 'border-l-blue-500',
  assessment: 'border-l-indigo-500',
  summary: 'border-l-purple-500',
  intake: 'border-l-teal-500',
  progress: 'border-l-cyan-500',
  discharge: 'border-l-rose-500',
  reflection: 'border-l-amber-500',
  other: 'border-l-gray-400',
};

/**
 * Normalize raw document_type to canonical category key
 * Template-specific names (SOAP, DAP) map to their parent category
 * @param {string} raw - Raw document_type value
 * @returns {string} Canonical category key
 */
// Note.document_type is a controlled enum (see utils/note_document_type.py).
// These are the ONLY values it can hold after the enum migration; they are
// classification keys, never user-facing titles.
export const DOCUMENT_TYPE_LABELS = {
  clinical: 'Clinical Note',
  risk_assessment: 'Risk Assessment',
  client_summary: 'Client Summary',
  private_process: 'Private Process Note',
  quick_note: 'Quick Note',
};

export const normalizeNoteType = (raw) => {
  if (!raw) return 'other';
  const lower = raw.toLowerCase().replace(/\s+/g, '_');
  
  if (['soap', 'soap_note', 'dap', 'dap_note', 'clinical', 'risk_assessment'].includes(lower)) return 'clinical';
  if (['progress', 'progress_note'].includes(lower)) return 'progress';
  if (['assessment'].includes(lower)) return 'assessment';
  if (['summary', 'client_summary', 'clientsummary'].includes(lower)) return 'summary';
  if (['intake', 'intake_note'].includes(lower)) return 'intake';
  if (['discharge', 'discharge_summary'].includes(lower)) return 'discharge';
  if (['therapistreflection', 'therapist_reflection', 'private_process'].includes(lower)) return 'reflection';
  
  return 'other';
};

/**
 * Get display label for a note.
 * Title comes from the note template name; document_type only supplies a
 * generic fallback label and is never rendered raw.
 * @param {string} documentType - Enum document_type value
 * @param {string} [sessionType] - Optional session_type override (e.g. "Yoga Session", "Administrative")
 * @param {string} [templateName] - Optional note template name (preferred title)
 * @returns {string} Human-readable display label
 */
export const getNoteTypeLabel = (documentType, sessionType = undefined, templateName = undefined) => {
  if (sessionType) return sessionType;
  if (templateName && templateName.trim()) return templateName.trim();
  if (documentType && DOCUMENT_TYPE_LABELS[documentType]) return DOCUMENT_TYPE_LABELS[documentType];
  const category = normalizeNoteType(documentType);
  if (category !== 'other') return CATEGORY_LABELS[category];
  // Non-note document types (e.g. SignatureRequest.document_type) still render.
  if (documentType && documentType.trim()) return documentType.trim();
  return 'Other';
};
